/**
 * Página de Observabilidade - Dashboard Admin
 * 
 * Sistema completo de observabilidade para monitorar todas as
 * requisições de API (envio/recebimento de mensagens) em todos
 * os módulos (Chat, Campanhas, Flows, Webhooks).
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Activity, ArrowDownLeft, ArrowUpRight, Clock, CheckCircle2, XCircle, AlertTriangle, Search, Eye, Trash2, Phone } from 'lucide-react';
import { 
  useObservabilityStats, 
  useApiRequestLogs, 
  useRequestVolume,
  useLatencyByProvider,
  useTenantsList,
  useSlowestApiRequests,
  type ObservabilityFilters,
  type ApiRequestLog 
} from '@/hooks/useObservability';
import { ApiRequestsSlowestItems } from '@/components/dashboard/ApiRequestsSlowestItems';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { logger } from '@/lib/logger';

export default function Observability() {
  // Estado de filtros
  const [filters, setFilters] = useState<ObservabilityFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [phoneSearchInput, setPhoneSearchInput] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [selectedLog, setSelectedLog] = useState<ApiRequestLog | null>(null);

  // Buscar dados
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useObservabilityStats(24);
  const { data: logsResult, isLoading: logsLoading, refetch: refetchLogs } = useApiRequestLogs(filters, page, pageSize);
  const { data: volumeData, isLoading: volumeLoading } = useRequestVolume(24);
  const { data: latencyData, isLoading: latencyLoading } = useLatencyByProvider(24);
  const { data: tenants } = useTenantsList();

  // Cores para gráficos
  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

  // Handlers
  const queryClient = useQueryClient();
  
  const handleRefresh = () => {
    refetchStats();
    refetchLogs();
    queryClient.invalidateQueries({ queryKey: ['api-requests-slowest'] });
    setLastUpdate(new Date());
    toast.success('Dados atualizados');
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput }));
    setPage(1);
  };

  // Handler para pesquisa por telefone
  const handlePhoneSearch = () => {
    setFilters(prev => ({ ...prev, phone: phoneSearchInput }));
    setPage(1);
  };

  const handleFilterChange = (key: keyof ObservabilityFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // Opções de período para limpeza de logs (em horas para granularidade)
  const cleanupOptions = [
    { value: '1', label: 'Última 1h', description: 'mais de 1 hora' },
    { value: '168', label: '7 dias', description: 'mais de 7 dias' },     // 7 * 24
    { value: '360', label: '15 dias', description: 'mais de 15 dias' },   // 15 * 24
    { value: '720', label: '30 dias', description: 'mais de 30 dias' },   // 30 * 24
    { value: '1440', label: '60 dias', description: 'mais de 60 dias' },  // 60 * 24
  ];
  const [cleanupPeriod, setCleanupPeriod] = useState('720'); // 30 dias padrão

  const handleCleanup = async () => {
    try {
      const hoursToKeep = parseInt(cleanupPeriod);
      const selectedOption = cleanupOptions.find(o => o.value === cleanupPeriod);
      
      // Usar nova função RPC que aceita horas
      const { data: deletedCount, error } = await supabase.rpc('clean_old_api_logs_v2', { 
        hours_to_keep: hoursToKeep 
      });
      
      if (error) throw error;

      // Feedback informativo sobre quantidade deletada
      if (deletedCount === 0) {
        toast.info(`Nenhum log encontrado ${selectedOption?.description || 'nesse período'}`);
      } else {
        toast.success(`${deletedCount} logs removidos (${selectedOption?.description || 'antigos'})`);
      }
      handleRefresh();
    } catch (error) {
      logger.error('[Observability] Erro ao limpar logs:', error);
      toast.error('Erro ao limpar logs antigos');
    }
  };

  // Renderização de badge de status
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle2 className="w-3 h-3 mr-1" />Sucesso</Badge>;
      case 'error':
        return <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20"><XCircle className="w-3 h-3 mr-1" />Erro</Badge>;
      case 'timeout':
        return <Badge variant="secondary" className="bg-orange-500/10 text-orange-600 border-orange-500/20"><AlertTriangle className="w-3 h-3 mr-1" />Timeout</Badge>;
      default:
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
    }
  };

  // Renderização de badge de provider
  const renderProviderBadge = (provider: string) => {
    // Mapa de cores por provider - ordenado por volume de uso
    const colors: Record<string, string> = {
      'z-api': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      'meta-whatsapp': 'bg-emerald-700/10 text-emerald-700 border-emerald-700/20',
      'waha': 'bg-teal-500/10 text-teal-600 border-teal-500/20',
      'notificame': 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      'meta-instagram': 'bg-pink-500/10 text-pink-600 border-pink-500/20',
      'webchat': 'bg-slate-500/10 text-slate-600 border-slate-500/20',
      'evolution': 'bg-green-500/10 text-green-600 border-green-500/20',
      'chatwoot': 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    };
    return <Badge variant="outline" className={colors[provider] || ''}>{provider}</Badge>;
  };

  // Renderização de badge de direção
  const renderDirectionBadge = (direction: string) => {
    if (direction === 'inbound') {
      return <Badge variant="outline" className="bg-cyan-500/10 text-cyan-600"><ArrowDownLeft className="w-3 h-3 mr-1" />Entrada</Badge>;
    }
    return <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600"><ArrowUpRight className="w-3 h-3 mr-1" />Saída</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Observabilidade</h1>
          <p className="text-muted-foreground">
            Monitoramento completo de requisições API
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Última atualização: {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: ptBR })}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-1">
            <Select value={cleanupPeriod} onValueChange={setCleanupPeriod}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {cleanupOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleCleanup}>
              <Trash2 className="mr-2 h-4 w-4" />
              Limpar
            </Button>
          </div>
          <Button onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requisições (24h)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : (stats?.totalRequests24h || 0).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.inboundCount || 0} entrada / {stats?.outboundCount || 0} saída
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statsLoading ? '...' : `${(stats?.successRate || 0).toFixed(1)}%`}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.errorRate ? `${stats.errorRate.toFixed(1)}% erros` : 'Sem erros'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Latência Média</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : `${Math.round(stats?.avgLatencyMs || 0)}ms`}
            </div>
            <p className="text-xs text-muted-foreground">
              Tempo de resposta
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Providers Ativos</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : (stats?.byProvider?.length || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.byProvider?.map(p => p.provider).join(', ') || 'Nenhum'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Volume por Hora */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Volume de Requisições (24h)</CardTitle>
            <CardDescription>Requisições por hora</CardDescription>
          </CardHeader>
          <CardContent>
            {volumeLoading ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">Carregando...</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={volumeData || []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="hour" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="success" name="Sucesso" fill="hsl(var(--chart-1))" stackId="a" />
                  <Bar dataKey="error" name="Erro" fill="hsl(var(--destructive))" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Latência por Provider */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Latência por Provider</CardTitle>
            <CardDescription>Tempo de resposta médio e P95</CardDescription>
          </CardHeader>
          <CardContent>
            {latencyLoading ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">Carregando...</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={latencyData || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" unit="ms" className="text-xs" />
                  <YAxis type="category" dataKey="provider" className="text-xs" width={80} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="avgLatency" name="Média" fill="hsl(var(--chart-2))" />
                  <Bar dataKey="p95Latency" name="P95" fill="hsl(var(--chart-3))" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Distribuição por Origem */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribuição por Origem</CardTitle>
            <CardDescription>Chat, Campanhas, Flows, Webhooks</CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading || !stats?.bySource?.length ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                {statsLoading ? 'Carregando...' : 'Sem dados'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.bySource}
                    dataKey="count"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ source, percent }) => `${source} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {stats.bySource.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Distribuição por Provider */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribuição por Provider</CardTitle>
            <CardDescription>Volume de requisições por provider</CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading || !stats?.byProvider?.length ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                {statsLoading ? 'Carregando...' : 'Sem dados'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.byProvider}
                    dataKey="count"
                    nameKey="provider"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ provider, percent }) => `${provider} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {stats.byProvider.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPI Top 20 - Requisições Mais Lentas */}
      <ApiRequestsSlowestItems />

      {/* Filtros e Tabela de Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Logs de Requisições</CardTitle>
          <CardDescription>Histórico detalhado de todas as operações com providers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filtros */}
          <div className="flex flex-wrap gap-2">
            <Select value={filters.provider || 'all'} onValueChange={(v) => handleFilterChange('provider', v)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Providers</SelectItem>
                <SelectItem value="z-api">Z-API</SelectItem>
                <SelectItem value="meta-whatsapp">Meta WhatsApp</SelectItem>
                <SelectItem value="waha">WAHA</SelectItem>
                <SelectItem value="notificame">Notificame</SelectItem>
                <SelectItem value="meta-instagram">Meta Instagram</SelectItem>
                <SelectItem value="webchat">Webchat</SelectItem>
                <SelectItem value="evolution">Evolution</SelectItem>
                <SelectItem value="chatwoot">Chatwoot</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.status || 'all'} onValueChange={(v) => handleFilterChange('status', v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="success">Sucesso</SelectItem>
                <SelectItem value="error">Erro</SelectItem>
                <SelectItem value="timeout">Timeout</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.source || 'all'} onValueChange={(v) => handleFilterChange('source', v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Origens</SelectItem>
                <SelectItem value="chat">Chat</SelectItem>
                <SelectItem value="campaign">Campanha</SelectItem>
                <SelectItem value="flow">Flow</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
                <SelectItem value="system">Sistema</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.direction || 'all'} onValueChange={(v) => handleFilterChange('direction', v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Direção" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Direções</SelectItem>
                <SelectItem value="inbound">Entrada</SelectItem>
                <SelectItem value="outbound">Saída</SelectItem>
              </SelectContent>
            </Select>

            {tenants && tenants.length > 0 && (
              <Select value={filters.tenantId || 'all'} onValueChange={(v) => handleFilterChange('tenantId', v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Tenants</SelectItem>
                  {tenants.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Input de busca por erro */}
            <div className="flex gap-1">
              <Input
                placeholder="Buscar por erro..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-[160px]"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="outline" size="icon" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Input de busca por telefone */}
            <div className="flex gap-1">
              <div className="relative">
                <Phone className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar telefone..."
                  value={phoneSearchInput}
                  onChange={(e) => setPhoneSearchInput(e.target.value)}
                  className="pl-8 w-[160px]"
                  onKeyDown={(e) => e.key === 'Enter' && handlePhoneSearch()}
                />
              </div>
              <Button variant="outline" size="icon" onClick={handlePhoneSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabela */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Operação</TableHead>
                  <TableHead>Direção</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Latência</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      Carregando logs...
                    </TableCell>
                  </TableRow>
                ) : !logsResult?.logs?.length ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      Nenhum log encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  logsResult.logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(log.created_at), 'dd/MM HH:mm:ss')}
                      </TableCell>
                      {/* Coluna Tenant - Exibe nome do tenant ou indicador quando ausente */}
                      <TableCell className="text-sm">
                        {log.tenant_name ? (
                          <span className="font-medium truncate max-w-[120px] block" title={log.tenant_name}>
                            {log.tenant_name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">Sistema</span>
                        )}
                      </TableCell>
                      <TableCell>{renderProviderBadge(log.provider_slug)}</TableCell>
                      <TableCell className="text-sm">{log.operation_type}</TableCell>
                      <TableCell>{renderDirectionBadge(log.direction)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{log.source_type}</Badge>
                      </TableCell>
                      <TableCell>{renderStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-sm">
                        {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          {logsResult && logsResult.total > 0 && (
            <PaginationFooter
              totalItems={logsResult.total}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Detalhes da Requisição
              {selectedLog && renderStatusBadge(selectedLog.status)}
            </DialogTitle>
            <DialogDescription>
              {selectedLog?.id}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList>
                <TabsTrigger value="info">Informações</TabsTrigger>
                <TabsTrigger value="request">Request</TabsTrigger>
                <TabsTrigger value="response">Response</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Provider</p>
                    <p className="font-medium">{selectedLog.provider_slug}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Operação</p>
                    <p className="font-medium">{selectedLog.operation_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Direção</p>
                    <p className="font-medium">{selectedLog.direction}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Origem</p>
                    <p className="font-medium">{selectedLog.source_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Endpoint</p>
                    <p className="font-medium break-all">{selectedLog.endpoint || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Latência</p>
                    <p className="font-medium">{selectedLog.response_time_ms ? `${selectedLog.response_time_ms}ms` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status HTTP</p>
                    <p className="font-medium">{selectedLog.response_status || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">External ID</p>
                    <p className="font-medium text-xs break-all">{selectedLog.external_id || '-'}</p>
                  </div>
                  {selectedLog.error_message && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Erro</p>
                      <p className="font-medium text-destructive">{selectedLog.error_message}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Criado em</p>
                    <p className="font-medium">{format(new Date(selectedLog.created_at), 'dd/MM/yyyy HH:mm:ss')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Completado em</p>
                    <p className="font-medium">{selectedLog.completed_at ? format(new Date(selectedLog.completed_at), 'dd/MM/yyyy HH:mm:ss') : '-'}</p>
                  </div>
                </div>

                {/* IDs relacionados */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Entidades Relacionadas</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Tenant:</span> {selectedLog.tenant_id || '-'}</div>
                    <div><span className="text-muted-foreground">Inbox:</span> {selectedLog.inbox_id || '-'}</div>
                    <div><span className="text-muted-foreground">Mensagem:</span> {selectedLog.message_id || '-'}</div>
                    <div><span className="text-muted-foreground">Conversa:</span> {selectedLog.conversation_id || '-'}</div>
                    <div><span className="text-muted-foreground">Contato:</span> {selectedLog.contact_id || '-'}</div>
                    <div><span className="text-muted-foreground">Campanha:</span> {selectedLog.campaign_id || '-'}</div>
                    <div><span className="text-muted-foreground">Flow:</span> {selectedLog.flow_id || '-'}</div>
                    <div><span className="text-muted-foreground">Correlation:</span> {selectedLog.correlation_id || '-'}</div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="request">
                <ScrollArea className="h-[400px] rounded-md border p-4">
                  <pre className="text-xs whitespace-pre-wrap">
                    {selectedLog.request_payload ? JSON.stringify(selectedLog.request_payload, null, 2) : 'Sem payload'}
                  </pre>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="response">
                <ScrollArea className="h-[400px] rounded-md border p-4">
                  <pre className="text-xs whitespace-pre-wrap">
                    {selectedLog.response_payload ? JSON.stringify(selectedLog.response_payload, null, 2) : 'Sem resposta'}
                  </pre>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
